import { zodResolver } from '@hookform/resolvers/zod';
import { authErrorMessage, DEV_ADMIN_EMAIL, DEV_ADMIN_USERNAME, SignInInput } from '@myhome/contracts';
import { Button } from '@myhome/ui/components/button';
import { Form } from '@myhome/ui/components/form';
import { SpinnerGap } from '@myhome/ui/icons';
import { StatusBanner } from '@myhome/ui/patterns/status-banner';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { AuthScreen, EmailField, FormError, PasswordField } from '../components/auth-screen';
import { authClient, safeRedirect, sessionQuery } from '../lib/auth-client';

const LoginSearch = z.object({
  redirect: z.string().optional().catch(undefined),
  notice: z.enum(['password-set', 'password-reset']).optional().catch(undefined),
});

export const Route = createFileRoute('/login')({
  validateSearch: (search) => LoginSearch.parse(search),
  component: LoginPage,
});

const NOTICES = {
  'password-set': {
    title: 'Your password is set',
    description: 'Sign in with your email address and the password you just chose.',
  },
  'password-reset': {
    title: 'Your password has been changed',
    description:
      'Sign in with your new password. To keep the account safe, we signed you out on every device.',
  },
} as const;

/**
 * In the dev build only, "admin" signs in as the seeded dev admin
 * (DEV_ADMIN_EMAIL, password admin). Production builds use SignInInput as is.
 */
const LoginInput = import.meta.env.DEV
  ? SignInInput.extend({
      email: z.preprocess(
        (value) =>
          typeof value === 'string' && value.trim() === DEV_ADMIN_USERNAME ? DEV_ADMIN_EMAIL : value,
        SignInInput.shape.email,
      ),
    })
  : SignInInput;

/**
 * Back-office sign in. Anyone can sign in here, because the cookie is shared
 * with app.; the shell then lets only ADMIN through, and the API checks the
 * role on every request.
 */
function LoginPage() {
  const { redirect, notice } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const form = useForm({
    resolver: zodResolver(LoginInput),
    defaultValues: { email: '', password: '' },
    mode: 'onBlur',
  });

  const onSubmit = form.handleSubmit(async ({ email, password }) => {
    const { data, error } = await authClient.signIn.email({ email, password });
    if (error !== null) {
      form.setError('root', { message: authErrorMessage(error) });
      form.resetField('password');
      return;
    }
    await queryClient.invalidateQueries({ queryKey: sessionQuery.queryKey });
    if (data.user.role !== 'ADMIN') {
      await navigate({ to: '/no-access', replace: true });
      return;
    }
    await navigate({ to: safeRedirect(redirect) ?? '/', replace: true });
  });

  const { isSubmitting, errors } = form.formState;

  return (
    <AuthScreen
      title="Back office"
      description="For MyHome+ staff. Members and providers sign in on the app."
    >
      {notice === undefined ? null : <StatusBanner tone="success" {...NOTICES[notice]} />}
      {import.meta.env.DEV ? (
        <StatusBanner
          tone="info"
          title="Development build"
          description="Sign in with admin and the password admin. Run pnpm db:seed if it does not work."
        />
      ) : null}
      <Form {...form}>
        <form noValidate onSubmit={onSubmit} className="flex flex-col gap-3">
          <FormError message={errors.root?.message} />
          <EmailField control={form.control} name="email" label="Email address" />
          <PasswordField
            control={form.control}
            name="password"
            label="Password"
            autoComplete="current-password"
          />
          <Button type="submit" block disabled={isSubmitting}>
            {isSubmitting ? (
              <SpinnerGap className="animate-spin motion-reduce:animate-none" aria-hidden="true" />
            ) : null}
            {isSubmitting ? 'Signing in' : 'Sign in'}
          </Button>
        </form>
      </Form>
      <Button asChild variant="link" className="min-h-(--spacing-touch) self-start">
        <Link to="/forgot-password">Forgot your password?</Link>
      </Button>
    </AuthScreen>
  );
}
